import React, { useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import FlashcardTable from "../../components/tables/FlashcardTable";
import PageMeta from "../../components/common/PageMeta";
import FlashcardService from "../../services/flashcard.service";
import Input from "../../components/form/input/InputField";
import { Search } from "lucide-react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

interface deck {
    _id: string;
    title: string;
}

interface Flashcard {
    _id: string;
    title_FC: string;
    type: string;
    createdAt: string;
    deck: deck[];
    flashcard_count: number;
    answer: string;
    image: string;
    sound: string;
}

export default function ListFlashcard() {
    const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [processedFlashcards, setProcessedFlashcards] = useState<Flashcard[]>([]);
    const { t } = useTranslation();

    const fetchFlashcards = async (page: number, search?: string) => {
        try {
            setLoading(true);
            setError("");
            const response = await FlashcardService.getListFlashCard({
                page: page,
                limit: 10,
                search: search,
            });

            setFlashcards(response.data.flashcards || []);
            setTotalPages(Math.ceil((response.data.count || 0) / 10));
            setCurrentPage(page);
        } catch (err) {
            setError("Failed to fetch flashcards. Please try again later.");
            console.error("Error fetching flashcards:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchFlashcards(1, searchQuery);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, []);

    useEffect(() => {
        const processFlashcards = async () => {
            const updated = await Promise.all(
                flashcards.map(async (fc) => {
                    const image = isValidURL(fc.image) ? fc.image : await fetchSignedUrl(fc.image);
                    const sound = isValidURL(fc.sound) ? fc.sound : await fetchSignedUrl(fc.sound);
                    return { ...fc, image, sound };
                })
            );
            setProcessedFlashcards(updated);
        };

        processFlashcards();
    }, [flashcards]);

    const handlePageChange = (page: number) => {
        fetchFlashcards(page, searchQuery);
    };

    const isValidURL = (url: string) => /^https?:\/\//i.test(url);

    const fetchSignedUrl = async (fileKey: string): Promise<any> => {
        try {
            const res = await FlashcardService.getFile({ fileKey });
            if (res?.data?.status == 200) {
                return res?.data?.fileUrl;
            }
        } catch (err) {
            console.error("Error fetching signed URL:", err);
            return '';
        }
    };

    return (
        <>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description="User Management"
            />
            <div className="space-y-6">
                <ComponentCard title="">
                    <div className="mb-4 relative">
                        <Input
                            type="text"
                            placeholder={`${t("Search flashcards")}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onEnter={(value) => fetchFlashcards(1, value)}
                            className="pl-10"
                        />
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                    {loading ? (
                        <div className="animate-pulse">
                            <div className="space-y-4">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className="flex items-center space-x-4 py-3">
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center p-4">{error}</div>
                    ) : (
                        <FlashcardTable
                            flashcards={processedFlashcards}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isLoading={loading}
                        />
                    )}
                </ComponentCard>
            </div>
        </>
    );
}
